/**
 * Performance Monitoring Utilities
 * Track Core Web Vitals and performance metrics
 */

import ReactGA from 'react-ga4';

// Core Web Vitals thresholds (in milliseconds)
export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  FID: { good: 100, needsImprovement: 300 },   // First Input Delay
  CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte
} as const;

/**
 * Report Web Vitals to Google Analytics
 */
export function sendWebVitalsToGA(metric: {
  name: string;
  value: number;
  delta: number;
  id: string;
}) {
  ReactGA.event({
    category: 'Web Vitals',
    action: metric.name,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    label: metric.id,
    nonInteraction: true,
  });
}

/**
 * Get performance rating based on thresholds
 */
export function getPerformanceRating(
  metricName: keyof typeof WEB_VITALS_THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = WEB_VITALS_THRESHOLDS[metricName];
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Measure page load time
 */
export function measurePageLoad(): number | null {
  if (typeof window === 'undefined' || !window.performance) return null;
  
  const perfData = window.performance.timing;
  const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
  
  return pageLoadTime;
}

/**
 * Measure time to first byte (TTFB)
 */
export function measureTTFB(): number | null {
  if (typeof window === 'undefined' || !window.performance) return null;
  
  const perfData = window.performance.timing;
  const ttfb = perfData.responseStart - perfData.navigationStart;
  
  return ttfb;
}

/**
 * Get connection information
 */
export function getConnectionInfo(): {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
} | null {
  if (typeof window === 'undefined') return null;
  
  const nav = navigator as any;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  
  if (!connection) return null;
  
  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

/**
 * Check if user is on slow connection
 */
export function isSlowConnection(): boolean {
  const connection = getConnectionInfo();
  if (!connection) return false;
  
  return (
    connection.effectiveType === '2g' ||
    connection.effectiveType === 'slow-2g' ||
    (connection.downlink !== undefined && connection.downlink < 1) ||
    connection.saveData === true
  );
}

/**
 * Prefetch critical resources
 */
export function prefetchResources(urls: string[]): void {
  if (typeof window === 'undefined') return;
  
  // Don't prefetch on slow connections
  if (isSlowConnection()) return;
  
  urls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'fetch';
    document.head.appendChild(link);
  });
}

/**
 * Preconnect to external domains
 */
export function preconnectDomains(domains: string[]): void {
  if (typeof window === 'undefined') return;
  
  domains.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Log performance metrics to console (dev only)
 */
export function logPerformanceMetrics(): void {
  if (process.env.NODE_ENV !== 'development') return;
  if (typeof window === 'undefined' || !window.performance) return;
  
  const perfData = window.performance.timing;
  
  console.group('⚡ Performance Metrics');
  console.log('Page Load Time:', measurePageLoad(), 'ms');
  console.log('TTFB:', measureTTFB(), 'ms');
  console.log('DOM Ready:', perfData.domContentLoadedEventEnd - perfData.navigationStart, 'ms');
  console.log('Connection:', getConnectionInfo());
  console.groupEnd();
}

/**
 * Monitor long tasks (tasks > 50ms)
 */
export function monitorLongTasks(callback: (entry: PerformanceEntry) => void): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        callback(entry);
      }
    });
    
    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    // Long task API not supported
  }
}

/**
 * Detect layout shifts
 */
export function monitorLayoutShifts(callback: (entry: any) => void): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          callback(entry);
        }
      }
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
  } catch (e) {
    // Layout shift API not supported
  }
}

/**
 * Setup performance monitoring
 */
export function setupPerformanceMonitoring(): void {
  if (typeof window === 'undefined') return;
  
  // Log metrics in development
  if (process.env.NODE_ENV === 'development') {
    window.addEventListener('load', () => {
      setTimeout(logPerformanceMetrics, 0);
    });
  }
  
  // Monitor long tasks
  monitorLongTasks((entry) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Long task detected:', entry.duration, 'ms');
    }
    
    ReactGA.event({
      category: 'Performance',
      action: 'Long Task',
      value: Math.round(entry.duration),
      nonInteraction: true,
    });
  });
  
  // Preconnect to important domains
  preconnectDomains([
    'https://api.abogalia.com.ar',
    'https://www.google-analytics.com',
    'https://www.clarity.ms',
  ]);
}

export default {
  measurePageLoad,
  measureTTFB,
  getConnectionInfo,
  isSlowConnection,
  prefetchResources,
  preconnectDomains,
  setupPerformanceMonitoring,
};

